import React from 'react';
import {
  Alert,
  Card,
  Space,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { formatCurrencyLocalized } from '../../../utils/currency';
import { formatDate } from '../../../utils/dateUtils';
import { formatUnit } from '../../../utils/units';
import { formatLocalizedPercent, localeForLanguage } from '../../../utils/numberFormatting';
import { OrderMarginExplanationDto } from '../api/ordersApi';
import { getOrderProductDisplayName } from '../orderProductDisplay';
import { hasRevenueForMarginPercent } from '../orderFinanceDisplay';
import './OrderMarginExplanationSection.css';
import {
  renderSectionTitle,
  SECTION_CARD_BODY_STYLE,
  SECTION_CARD_HEAD_STYLE,
  SECTION_CARD_STYLE,
} from '../orderDetailLayout';

interface OrderMarginExplanationSectionProps {
  marginExplanation: OrderMarginExplanationDto | null;
  loading: boolean;
}

export const OrderMarginExplanationSection: React.FC<OrderMarginExplanationSectionProps> = ({
  marginExplanation,
  loading,
}) => {
  const { t, i18n } = useTranslation(['orders', 'transport']);
  const formatMoney = (value: number | string | null | undefined) =>
    formatCurrencyLocalized(value, 'PLN', i18n.language);
  const lines = marginExplanation?.lines || [];
  const unassignedAllocations = marginExplanation?.unassignedAllocations || [];
  const orderLevelCostPLN = marginExplanation
    ? Number((marginExplanation.orderLevelCosts.cogsPLN + marginExplanation.orderLevelCosts.manualCostPLN).toFixed(2))
    : 0;
  const hasUnassignedSupplierCost = Boolean(
    marginExplanation && Math.abs(marginExplanation.totals.unassignedAllocationCostPLN) > 0.005
  );
  const unavailableValue = <span style={{ color: '#94a3b8' }}>—</span>;
  const exchangeRateLabel = marginExplanation?.order.exchangeRate == null
    ? null
    : Number(marginExplanation.order.exchangeRate).toLocaleString(localeForLanguage(i18n.language), {
        minimumFractionDigits: 4,
        maximumFractionDigits: 6,
      });
  const exchangeRateSourceLabel = marginExplanation?.order.exchangeRateSource
    ? t(`transport:allocation.costProvenanceSources.${marginExplanation.order.exchangeRateSource}`, {
        defaultValue: marginExplanation.order.exchangeRateSource,
      })
    : null;

  return (
    <Card
      title={renderSectionTitle(t('orders:orderDetail.marginExplanation'))}
      style={SECTION_CARD_STYLE}
      styles={{ header: SECTION_CARD_HEAD_STYLE, body: SECTION_CARD_BODY_STYLE }}
      loading={loading}
    >
      {marginExplanation ? (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {marginExplanation.order.exchangeRate && (
            <Alert
              type="info"
              showIcon
              message={t('orders:orderDetail.exchangeRateSnapshot')}
              description={[
                `${marginExplanation.order.currency} -> PLN: ${exchangeRateLabel}`,
                marginExplanation.order.exchangeRateEffectiveDate
                  ? `${t('orders:orderDetail.rateFrom')} ${formatDate(marginExplanation.order.exchangeRateEffectiveDate)}`
                  : null,
                exchangeRateSourceLabel,
                marginExplanation.order.exchangeRateTableNumber || null,
              ].filter(Boolean).join(' · ')}
            />
          )}
          {!marginExplanation.totals.reconciles && (
            <Alert
              type="warning"
              showIcon
              message={t('orders:orderDetail.marginExplanationUnreconciled')}
            />
          )}
          {hasUnassignedSupplierCost && (
            <Alert
              type="warning"
              showIcon
              message={t('orders:orderDetail.unassignedAllocationWarningTitle')}
              description={t('orders:orderDetail.unassignedAllocationWarningDescription')}
            />
          )}
          {hasUnassignedSupplierCost && (
            <div className="order-margin-unassigned-list">
              {unassignedAllocations.map((allocation) => (
                <div key={allocation.linkId || `${allocation.supplierOrderId}-${allocation.supplierOrderItemId}`}>
                  <strong>{allocation.supplierOrderNumber || t('orders:orderDetail.unknownSupplierOrder')}</strong>
                  <span>{allocation.supplierName || t('orders:orderDetail.unknownSupplier')}</span>
                  <span>{allocation.supplierOrderItemName || t('orders:orderDetail.wholeSupplierOrder')}</span>
                  <strong>{formatMoney(allocation.effectiveAllocatedCostPLN)}</strong>
                </div>
              ))}
            </div>
          )}
          {orderLevelCostPLN !== 0 && (
            <Alert
              type="info"
              showIcon
              message={t('orders:orderDetail.lineResultsExcludeOrderLevelCosts')}
            />
          )}
          {lines.length === 0 ? (
            <Alert type="info" message={t('orders:orderDetail.noMarginExplanationLines')} />
          ) : (
            <div className="order-margin-line-list">
              {lines.map((line) => (
                <article className="order-margin-line-card" key={line.id}>
                  <header>
                    <strong>{getOrderProductDisplayName(line, line.name)}</strong>
                    <span>{line.saleQuantity.toLocaleString(localeForLanguage(i18n.language))} {formatUnit(line.saleUnit, i18n.language)}</span>
                  </header>
                  <div className="order-margin-line-card__metrics">
                    {[
                      [t('orders:orderDetail.revenue'), formatMoney(line.revenuePLN)],
                      [t('orders:orderDetail.purchaseCost'), hasUnassignedSupplierCost ? unavailableValue : formatMoney(line.supplierPurchaseCostPLN)],
                      [t('orders:orderDetail.landedCost'), hasUnassignedSupplierCost ? unavailableValue : formatMoney(line.supplierLandedCostPLN)],
                      [t('orders:orderDetail.caseATotalCost'), hasUnassignedSupplierCost ? unavailableValue : formatMoney(line.allocatedCostPLN)],
                      [t('orders:orderDetail.attributedLineResultBeforeOrderCosts'), hasUnassignedSupplierCost
                        ? unavailableValue
                        : `${formatMoney(line.marginPLN)} (${hasRevenueForMarginPercent(line.revenuePLN) ? formatLocalizedPercent(line.marginPercent, i18n.language) : t('orders:orderDetail.marginPercentUnavailable')})`],
                    ].map(([label, value]) => (
                      <div key={String(label)}>
                        <span>{label}</span>
                        <strong>{value}</strong>
                      </div>
                    ))}
                  </div>
                  {line.allocations.length > 0 && (
                    <details className="order-margin-line-card__allocations">
                      <summary>{t('orders:orderDetail.supplierAllocationDetails', { count: line.allocations.length })}</summary>
                      {line.allocations.map((allocation) => (
                        <div key={allocation.linkId || `${allocation.supplierOrderId}-${allocation.supplierOrderItemId}`}>
                          <span>{allocation.supplierOrderNumber || '—'} · {allocation.supplierName || '—'}</span>
                          <span>{allocation.supplierOrderItemName || '—'}</span>
                          <strong>{formatMoney(allocation.effectiveAllocatedCostPLN)}</strong>
                        </div>
                      ))}
                    </details>
                  )}
                </article>
              ))}
            </div>
          )}
        </Space>
      ) : (
        <Alert type="info" showIcon message={t('orders:orderDetail.marginExplanationUnavailable')} />
      )}
    </Card>
  );
};
